import { useEffect, useState } from "react";
import { MapPin, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  hasSeenLocationPrompt,
  markLocationPromptShown,
  getStoredCoords,
  requestBrowserLocation,
} from "@/lib/geolocation";
import { toast } from "sonner";
import { isAutoLocationEnabled } from "@/hooks/use-user-location";

export function LocationPrompt() {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Show once per browser, only if not already seen and no coords stored
    const timer = setTimeout(() => {
      if (isAutoLocationEnabled() && !hasSeenLocationPrompt() && !getStoredCoords()) {
        setVisible(true);
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    markLocationPromptShown();
    setVisible(false);
  };

  const allow = async () => {
    setLoading(true);
    try {
      await requestBrowserLocation();
      toast.success("Location detected — showing nearby providers first");
      markLocationPromptShown();
      setVisible(false);
      // Notify listeners (e.g. Browse page) that coords are now available
      window.dispatchEvent(new Event("taskratel:coords-updated"));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Location unavailable";
      toast.error(`Couldn't get location: ${msg}`);
      markLocationPromptShown();
      setVisible(false);
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-2 bottom-20 z-[55] animate-in slide-in-from-bottom-4 fade-in md:inset-x-auto md:bottom-6 md:right-6 md:max-w-sm">
      <Card className="border-primary/40 bg-card shadow-xl">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-primary/10 p-2">
              <MapPin className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-sm">Find pros near you</h4>
              <p className="mt-1 text-xs text-muted-foreground">
                Share your location to see the closest verified professionals first.
              </p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={allow} disabled={loading}>
                  {loading ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                  Allow
                </Button>
                <Button size="sm" variant="ghost" onClick={dismiss} disabled={loading}>
                  Not now
                </Button>
              </div>
            </div>
            <button
              onClick={dismiss}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}